/**
 * Preview Mode Indicator - Auto-injected DOM element
 * 
 * Automatically injects a visual indicator when SDK is in preview/draft mode
 * Works with vanilla JS/DOM - no React required
 */

import { isDraftMode, isFunnelTrackingEnabled, getSDKParams, setDraftMode, setFunnelTracking, clearClientEnvironment, clearClientBaseUrl } from './previewMode';
import { clearClientToken } from './tokenStorage';
import { clearFunnelSessionCookie } from './sessionStorage';

let indicatorElement: HTMLElement | null = null;
let isInjected = false;

/**
 * Helper to clear a specific cookie with all possible domain/path combinations
 */
function clearSpecificCookie(cookieName: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // All possible domains
    const domains = [
        '', // No domain (current)
        hostname, // Full hostname
        '.' + hostname, // Wildcard current
    ];

    // Add parent domains
    for (let i = 1; i < parts.length; i++) {
        const domain = parts.slice(i).join('.');
        domains.push(domain);
        domains.push('.' + domain);
    }

    // All possible paths
    const pathParts = window.location.pathname.split('/').filter(p => p);
    const paths = ['/'];
    let currentPath = '';
    pathParts.forEach(part => {
        currentPath += '/' + part;
        paths.push(currentPath);
    });

    // Try all combinations
    domains.forEach(domain => {
        paths.forEach(path => {
            const baseDelete = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
            const domainPart = domain ? `; domain=${domain}` : '';

            // Try all security flag variations
            document.cookie = baseDelete + domainPart;
            document.cookie = baseDelete + domainPart + '; secure';
            document.cookie = baseDelete + domainPart + '; SameSite=None; secure';
            document.cookie = baseDelete + domainPart + '; SameSite=Lax';
            document.cookie = baseDelete + domainPart + '; SameSite=Strict';
        });
    });
}

/**
 * Leave preview mode - Clear ALL storage (including Shopify cookies, all localStorage, etc.) and reload
 * ⚠️ Nuclear option - clears everything to exit preview mode completely
 */
function leavePreviewMode(): void {
    if (typeof window === 'undefined') return;

    const confirmed = confirm(
        '🚪 Leave Preview Mode?\n\nThis will clear ALL cookies and localStorage (including Shopify session, cart, and preview settings) and reload the page.\n\nAre you sure?'
    );

    if (!confirmed) return;

    // STEP 0: Remove ALL preview params from URL immediately
    const url = new URL(window.location.href);
    const previewParams = [
        'draft', 'funnelEnv', 'funnelTracking', 'tagadaClientEnv',
        'tagadaClientBaseUrl', 'forceReset', 'funnelId', 'funnelSessionId', 'token',
    ];

    previewParams.forEach(param => url.searchParams.delete(param));
    window.history.replaceState({}, '', url.href);

    // STEP 0.5: Install global cookie blocker for tgd_draft
    try {
        const cookieDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
            Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');

        if (cookieDescriptor && cookieDescriptor.set) {
            Object.defineProperty(document, 'cookie', {
                configurable: true,
                enumerable: true,
                get: function () { return cookieDescriptor.get ? cookieDescriptor.get.call(this) : ''; },
                set: function (val) {
                    if (typeof val === 'string') {
                        const normalizedVal = val.toLowerCase();
                        if (normalizedVal.includes('tgd_draft') ||
                            normalizedVal.includes('tgd-draft') ||
                            normalizedVal.includes('tgd.draft') ||
                            normalizedVal.includes('tgddraft')) {

                            // Allow deletion attempts
                            if (normalizedVal.includes('max-age=0') ||
                                normalizedVal.includes('expires=thu, 01 jan 1970')) {
                                if (cookieDescriptor.set) {
                                    cookieDescriptor.set.call(this, val);
                                }
                                return;
                            }

                            // Block all creation/update attempts
                            console.warn('🛡️ [TagadaPay] BLOCKED: tgd_draft should never be a cookie');
                            return;
                        }
                    }

                    if (cookieDescriptor.set) {
                        cookieDescriptor.set.call(this, val);
                    }
                }
            });
        }
    } catch (e) {
        console.warn('[TagadaPay] Could not install cookie blocker:', e);
    }

    // STEP 1: Clear TagadaPay localStorage keys
    if (window.localStorage) {
        try {
            localStorage.removeItem('tgd_draft');
            localStorage.removeItem('tgd_funnel_tracking');
            localStorage.removeItem('tgd_client_env');
            localStorage.removeItem('tgd_client_base_url');
            localStorage.removeItem('cms_token');
        } catch (e) {
            console.warn('[TagadaPay] Failed to clear some localStorage keys:', e);
        }
    }

    // Clear SDK utilities
    clearClientToken();
    clearFunnelSessionCookie();
    clearClientEnvironment();
    clearClientBaseUrl();

    // STEP 2: Clear ALL storage
    if (window.localStorage) {
        localStorage.clear();
    }
    if (window.sessionStorage) {
        sessionStorage.clear();
    }

    // STEP 3: Clear known TagadaPay cookies
    const tagadaCookies = [
        'tgd-funnel-session-id', 'tgd_draft', 'tgd_funnel_tracking',
        'tgd_client_env', 'tgd_client_base_url', 'cms_token', 'tagadapay_session',
    ];

    tagadaCookies.forEach(cookieName => clearSpecificCookie(cookieName));

    // STEP 3.5: Clear alternative cookie name formats
    const alternativeNames = ['tgd-draft', 'tgd_draft', 'tgd.draft', 'tgddraft'];
    alternativeNames.forEach(cookieName => clearSpecificCookie(cookieName));

    // STEP 4: Clear ALL remaining cookies aggressively
    if (document.cookie) {
        const cookies = document.cookie.split(';');
        cookies.forEach(cookie => {
            const cookieName = cookie.split('=')[0].trim();
            if (!cookieName) return;

            const hostname = window.location.hostname;
            const parts = hostname.split('.');
            const domains = ['', hostname, '.' + hostname];

            for (let i = 1; i < parts.length; i++) {
                const domain = parts.slice(i).join('.');
                domains.push(domain);
                domains.push('.' + domain);
            }

            const pathParts = window.location.pathname.split('/').filter(p => p);
            const paths = ['/'];
            let currentPath = '';
            pathParts.forEach(part => {
                currentPath += '/' + part;
                paths.push(currentPath);
            });

            domains.forEach(domain => {
                paths.forEach(path => {
                    const baseDelete = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
                    const domainPart = domain ? `; domain=${domain}` : '';

                    document.cookie = baseDelete + domainPart;
                    document.cookie = baseDelete + domainPart + '; secure';
                    document.cookie = baseDelete + domainPart + '; SameSite=None; secure';
                    document.cookie = baseDelete + domainPart + '; SameSite=Lax';
                    document.cookie = baseDelete + domainPart + '; SameSite=Strict';
                    document.cookie = baseDelete + domainPart + '; secure; SameSite=None';
                    document.cookie = baseDelete + domainPart + '; secure; SameSite=Lax';
                    document.cookie = baseDelete + domainPart + '; secure; SameSite=Strict';
                });
            });
        });
    }

    // Final cleanup and reload
    setTimeout(() => {
        if (window.localStorage && localStorage.length > 0) {
            localStorage.clear();
        }

        clearSpecificCookie('tgd_draft');

        if (typeof window.stop === 'function') {
            window.stop();
        }

        window.location.replace(url.href);
    }, 10);
}

/**
 * Inject the preview mode indicator into the DOM
 * Called automatically during SDK initialization if preview mode is active
 */
export function injectPreviewModeIndicator(): void {
    // Skip if already injected or not in browser
    if (isInjected || typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }

    const params = getSDKParams();
    const draftMode = isDraftMode();
    const trackingDisabled = !isFunnelTrackingEnabled();
    const hasCustomEnv = !!(params.tagadaClientEnv || params.tagadaClientBaseUrl);

    // Only inject if in preview/dev mode
    if (!draftMode && !trackingDisabled && !hasCustomEnv) {
        return;
    }

    // Create container
    const container = document.createElement('div');
    container.id = 'tgd-preview-indicator';
    container.style.cssText = `
        position: fixed;
        bottom: 16px;
        right: 16px;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    `;

    // Create badge
    const badge = document.createElement('div');
    badge.style.cssText = `
        background: ${draftMode ? '#ff9500' : '#007aff'};
        color: white;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 6px;
    `;
    badge.innerHTML = `
        <span style="font-size: 16px;">🔍</span>
        <span>${draftMode ? 'Preview Mode' : 'Dev Mode'}</span>
    `;

    // Create details popup (with padding-top to bridge gap with badge)
    const details = document.createElement('div');
    details.style.cssText = `
        position: absolute;
        bottom: calc(100% + 8px);
        right: 0;
        background: white;
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        padding: 12px;
        min-width: 250px;
        font-size: 12px;
        line-height: 1.5;
        display: none;
    `;
    details.style.paddingTop = '20px'; // Extra padding to bridge the gap

    // Add invisible bridge between badge and popup to prevent flickering
    const bridge = document.createElement('div');
    bridge.style.cssText = `
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        height: 8px;
        display: none;
    `;

    // Build details content
    let detailsHTML = '<div style="margin-bottom: 8px; font-weight: 600; color: #1d1d1f;">Current Environment</div>';
    detailsHTML += '<div style="display: flex; flex-direction: column; gap: 6px;">';

    if (draftMode) {
        detailsHTML += `
            <div style="display: flex; justify-content: space-between; color: #86868b;">
                <span>Draft Mode:</span>
                <span style="color: #ff9500; font-weight: 600;">ON</span>
            </div>
        `;
    }

    if (trackingDisabled) {
        detailsHTML += `
            <div style="display: flex; justify-content: space-between; color: #86868b;">
                <span>Tracking:</span>
                <span style="color: #ff3b30; font-weight: 600;">DISABLED</span>
            </div>
        `;
    }

    if (params.funnelEnv) {
        detailsHTML += `
            <div style="display: flex; justify-content: space-between; color: #86868b;">
                <span>Funnel Env:</span>
                <span style="color: #1d1d1f; font-weight: 600; font-family: monospace; font-size: 11px;">
                    ${params.funnelEnv}
                </span>
            </div>
        `;
    }

    if (params.tagadaClientEnv) {
        detailsHTML += `
            <div style="display: flex; justify-content: space-between; color: #86868b;">
                <span>API Env:</span>
                <span style="color: #1d1d1f; font-weight: 600; font-family: monospace; font-size: 11px;">
                    ${params.tagadaClientEnv}
                </span>
            </div>
        `;
    }

    if (params.tagadaClientBaseUrl) {
        detailsHTML += `
            <div style="color: #86868b;">
                <div style="margin-bottom: 4px;">API URL:</div>
                <div style="color: #1d1d1f; font-weight: 600; font-family: monospace; font-size: 10px; word-break: break-all; background: #f5f5f7; padding: 4px 6px; border-radius: 4px;">
                    ${params.tagadaClientBaseUrl}
                </div>
            </div>
        `;
    }

    if (params.funnelId) {
        detailsHTML += `
            <div style="color: #86868b; margin-top: 4px; padding-top: 8px; border-top: 1px solid #e5e5e5;">
                <div style="margin-bottom: 4px;">Funnel ID:</div>
                <div style="color: #1d1d1f; font-family: monospace; font-size: 10px; word-break: break-all; background: #f5f5f7; padding: 4px 6px; border-radius: 4px;">
                    ${params.funnelId}
                </div>
            </div>
        `;
    }

    detailsHTML += '</div>';
    detailsHTML += `
        <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e5e5; font-size: 11px; color: #86868b; text-align: center;">
            Add <code style="background: #f5f5f7; padding: 2px 4px; border-radius: 3px;">?forceReset=true</code> to reset
        </div>
    `;

    // Add action button
    detailsHTML += `
        <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e5e5e5;">
            <button id="tgd-leave-preview" style="
                background: #ff3b30;
                color: white;
                border: none;
                border-radius: 6px;
                padding: 10px 12px;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
                transition: opacity 0.2s;
                width: 100%;
            ">
                🚪 Leave Preview Mode
            </button>
        </div>
    `;

    details.innerHTML = detailsHTML;

    // Hover behavior - keep popup visible when hovering over badge, bridge, or popup
    let isHovering = false;

    const showDetails = () => {
        isHovering = true;
        details.style.display = 'block';
        bridge.style.display = 'block';
    };

    const hideDetails = () => {
        isHovering = false;
        setTimeout(() => {
            if (!isHovering) {
                details.style.display = 'none';
                bridge.style.display = 'none';
            }
        }, 100); // Small delay to allow moving between elements
    };

    badge.addEventListener('mouseenter', showDetails);
    badge.addEventListener('mouseleave', hideDetails);
    bridge.addEventListener('mouseenter', showDetails);
    bridge.addEventListener('mouseleave', hideDetails);
    details.addEventListener('mouseenter', showDetails);
    details.addEventListener('mouseleave', hideDetails);

    // Button: Leave preview mode
    const leavePreviewBtn = details.querySelector('#tgd-leave-preview') as HTMLButtonElement;
    if (leavePreviewBtn) {
        leavePreviewBtn.addEventListener('mouseenter', () => {
            leavePreviewBtn.style.opacity = '0.8';
        });
        leavePreviewBtn.addEventListener('mouseleave', () => {
            leavePreviewBtn.style.opacity = '1';
        });
        leavePreviewBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            leavePreviewMode();
        });
    }

    // Assemble
    container.appendChild(badge);
    container.appendChild(bridge); // Add invisible bridge
    container.appendChild(details);

    // Inject into DOM
    document.body.appendChild(container);
    indicatorElement = container;
    isInjected = true;
}

/**
 * Remove the preview mode indicator from the DOM
 */
export function removePreviewModeIndicator(): void {
    if (indicatorElement && indicatorElement.parentNode) {
        indicatorElement.parentNode.removeChild(indicatorElement);
        indicatorElement = null;
        isInjected = false;
    }
}

/**
 * Check if indicator is currently injected
 */
export function isIndicatorInjected(): boolean {
    return isInjected;
}

